import { Permissions } from "utils/permissions/permissionConstants";

/**
 * Workspace-related types and interfaces
 * Based on RBAC v1 backend implementation
 */

// Workspace Role Enum (from backend)
export enum WorkspaceRole {
  OWNER = "owner",
  ADMIN = "admin",
  MEMBER = "member",
  GUEST = "guest",
  CUSTOM = "custom",
}

export interface WorkspaceCustomRole {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  permissions: string[];
  is_archived: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Workspace Member Status
export enum WorkspaceMemberStatus {
  ACTIVE = "active",
  PENDING = "pending",
  INACTIVE = "inactive",
}

// Workspace Permissions - Function to check if permission exists
export type WorkspacePermissions = (permission: string) => boolean;

// Workspace Role Permissions (from backend) - MUST match backend src/authz/policies/workspace.policy.ts

export const WorkspaceRolePolicy: Record<WorkspaceRole, string[]> = {
  [WorkspaceRole.OWNER]: [
    Permissions.WORKSPACE_READ,
    Permissions.WORKSPACE_UPDATE,
    Permissions.WORKSPACE_MEMBER_LIST,
    Permissions.WORKSPACE_MEMBER_INVITE,
    Permissions.WORKSPACE_MEMBER_UPDATE,
    Permissions.WORKSPACE_MEMBER_REMOVE,
    Permissions.WORKSPACE_BILLING_READ,
    Permissions.WORKSPACE_BILLING_UPDATE,
    Permissions.WORKSPACE_PROJECT_LIST,
    Permissions.WORKSPACE_PROJECT_CREATE,
    Permissions.WORKSPACE_API_KEY_LIST,
    Permissions.WORKSPACE_API_KEY_MANAGE,
    Permissions.WORKSPACE_SSO_MANAGE,
  ],
  [WorkspaceRole.ADMIN]: [
    Permissions.WORKSPACE_READ,
    Permissions.WORKSPACE_UPDATE,
    Permissions.WORKSPACE_MEMBER_LIST,
    Permissions.WORKSPACE_MEMBER_INVITE,
    Permissions.WORKSPACE_MEMBER_UPDATE,
    Permissions.WORKSPACE_MEMBER_REMOVE,
    Permissions.WORKSPACE_PROJECT_LIST,
    Permissions.WORKSPACE_PROJECT_CREATE,
    Permissions.WORKSPACE_API_KEY_LIST,
    Permissions.WORKSPACE_API_KEY_MANAGE,
    Permissions.WORKSPACE_SSO_MANAGE,
  ],
  [WorkspaceRole.MEMBER]: [
    Permissions.WORKSPACE_READ,
    Permissions.PROJECT_READ,
    Permissions.WORKSPACE_API_KEY_LIST,
  ],
  [WorkspaceRole.GUEST]: [],
  [WorkspaceRole.CUSTOM]: [],
};

export const WORKSPACE_PRESET_ROLES: WorkspaceRole[] = [
  WorkspaceRole.OWNER,
  WorkspaceRole.ADMIN,
  WorkspaceRole.MEMBER,
  WorkspaceRole.GUEST,
];

export const formatWorkspaceRoleLabel = (
  role: WorkspaceRole,
  customRoleName?: string | null
): string => {
  if (role === WorkspaceRole.CUSTOM && customRoleName) {
    return customRoleName;
  }
  switch (role) {
    case WorkspaceRole.OWNER:
      return "Owner";
    case WorkspaceRole.ADMIN:
      return "Admin";
    case WorkspaceRole.MEMBER:
      return "Member";
    case WorkspaceRole.GUEST:
      return "Guest";
    case WorkspaceRole.CUSTOM:
      return "Custom role";
    default:
      return role;
  }
};

// Workspace Member Payload (from API) - Updated to match backend schema
export interface WorkspaceMemberPayload {
  id: string | null; // User ID for active members, null for pending invitations
  role: WorkspaceRole;
  custom_role_id?: string | null;
  custom_role_name?: string | null;
  created_at: string;
  updated_at: string;
  last_login_at?: string | null;
  // Flattened user fields (industry standard)
  // For pending invitations, these fields are null/false
  first_name: string | null;
  last_name: string | null;
  email?: string | null; // May be null for pending invitations
  invitee_email?: string | null; // Used for pending invitations
  position_title: string | null;
  company_name: string | null;
  profile_image_path?: string | null;
  is_active: boolean;
  verified: boolean;
  // Project access fields (from workspace endpoint)
  all_project_access: boolean;
  project_ids: string[] | null;
  // Invitation status (for pending invitations in members array)
  invitation_status?: "active" | "pending" | null;
  invitation_id?: string | null;
  expires_at?: string | null;
  display_name?: string | null; // Display name provided by inviter for pending invitations (optional)
  /** Additive workspace permission grants (API); union with role at authz time */
  extra_workspace_permissions?: string[] | null;
  /** Pending invitations: SendGrid result for the invite email ("bounced", "delivered", ...) */
  email_status?: string | null;
  email_status_reason?: string | null;
}

// Transformed Workspace Member (for UI)
export interface WorkspaceMember {
  id: string | null; // null for pending invitations
  role: WorkspaceRole;
  customRoleId?: string | null;
  customRoleName?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  positionTitle: string;
  companyName: string;
  profileImagePath?: string;
  isActive: boolean;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string | null;
  fullName: string;
  displayName: string | null; // Can be null for pending invitations without display_name
  // Project access fields
  allProjectAccess: boolean;
  projectIds: string[];
  // Invitation status (for pending invitations in members array)
  invitationStatus?: "active" | "pending" | null;
  invitationId?: string | null; // invitation_id for pending invitations (UUID)
  extraWorkspacePermissions?: string[];
  emailStatus?: string | null; // pending invitations: SendGrid result for the invite email
  emailStatusReason?: string | null;
}

// Pending Invitation Payload (from API)
export interface PendingInvitationPayload {
  invitation_id: string;
  invitee_email?: string;
  email?: string; // API may return 'email' instead of 'invitee_email'
  invitee_id: string | null;
  role: string; // Project role: "creator", "editor", "viewer", or "trial"
  workspace_role?: string | null; // Workspace role: "admin", "member", or "guest" (optional)
  all_project_access: boolean;
  project_ids: string[] | null;
  status: "pending";
  expires_at: string; // ISO 8601 datetime
  created_at: string; // ISO 8601 datetime
  display_name?: string | null; // Display name provided by inviter (optional)
  position_title?: string | null; // Job title (optional)
  extra_workspace_permissions?: string[] | null;
}

// Transformed Pending Invitation (for UI)
export interface PendingInvitation {
  invitationId: string;
  inviteeEmail: string;
  inviteeId: string | null;
  role: string; // Project role
  workspaceRole: string | null; // Workspace role
  allProjectAccess: boolean;
  projectIds: string[] | null;
  status: "pending";
  expiresAt: string;
  createdAt: string;
  displayName?: string | null; // Display name provided by inviter (optional)
  positionTitle?: string | null; // Job title (optional)
  extraWorkspacePermissions?: string[];
  emailStatus?: string | null; // SendGrid result for the invite email ("bounced", "delivered", ...)
  emailStatusReason?: string | null;
}

// Workspace Payload (from API)
export interface WorkspacePayload {
  id: string;
  name: string;
  image_path?: string;
  contribute_budget_benchmarks?: boolean;
  created_at: string;
  updated_at: string;
  members?: WorkspaceMemberPayload[];
  /**
   * Accepted members in the workspace. Callers without workspace:member:list get only their
   * own row in `members`, so count from this, not from `members.length`.
   */
  member_count?: number;
  pending_invitations?: PendingInvitationPayload[];
  permissions?: {
    permissions: string[];
    role: string;
    workspace_id: string;
    user_id: string;
  };
}

// Transformed Workspace (for UI)
export interface Workspace {
  id: string;
  name: string;
  imagePath?: string;
  contributeBudgetBenchmarks: boolean;
  createdAt: string;
  updatedAt: string;
  members?: WorkspaceMember[];
  /** Accepted member count. `members` may hold only the viewer's own row. */
  memberCount?: number;
  pendingInvitations?: PendingInvitation[];
  permissions?: {
    permissions: string[];
    role: string;
    workspaceId: string;
    userId: string;
  };
}

// Inviter User (for invitation relations)
export interface InviterUser {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_image_path?: string | null;
}

// Workspace Invitation Payload (from API)
export interface WorkspaceInvitationPayload {
  id: string;
  workspace_id: string;
  email: string;
  role: WorkspaceRole;
  status: "pending" | "accepted" | "cancelled" | "expired";
  invited_by: string;
  inviter?: InviterUser;
  created_at: string;
  updated_at: string;
  expires_at: string;
  token?: string;
}

// Transformed Workspace Invitation (for UI)
export interface WorkspaceInvitation {
  id: string;
  workspaceId: string;
  email: string;
  role: WorkspaceRole;
  status: "pending" | "accepted" | "cancelled" | "expired";
  invitedBy: string;
  inviter?: InviterUser;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  token?: string;
}

// Workspace Filters
export interface WorkspaceFilters {
  page?: number;
  take?: number;
  order?: string;
  search?: string;
  role?: WorkspaceRole;
  status?: WorkspaceMemberStatus;
}

// Workspace Counts
export interface WorkspaceCounts {
  total: number;
  active: number;
  pending: number;
  inactive: number;
}

// Invite Workspace Member Parameters
export interface InviteWorkspaceMemberParams {
  email: string;
  role: WorkspaceRole;
  message?: string;
  displayName?: string; // Optional display name for personalization
  positionTitle?: string; // Optional job title
}

// Update Workspace Member Parameters
export interface UpdateWorkspaceMemberParams {
  memberId: string;
  role: WorkspaceRole;
  positionTitle?: string;
}

// Update Workspace DTO (from backend)
export interface UpdateWorkspaceDto {
  name?: string;
  image_path?: string;
  contribute_budget_benchmarks?: boolean;
}

// Update Workspace Member DTO (from backend)
export interface UpdateWorkspaceMemberDto {
  role: WorkspaceRole;
}

// Remove Workspace Member Parameters
export interface RemoveWorkspaceMemberParams {
  memberId: string;
  reason?: string;
}

// Update Workspace Invitation Parameters
export interface UpdateWorkspaceInvitationParams {
  invitationId: string;
  role: WorkspaceRole;
  message?: string;
}

// Workspace API Response Types
export interface WorkspaceListResponse {
  data: WorkspacePayload[];
  meta: {
    page: number;
    take: number;
    item_count: number;
    total: number;
    page_count: number;
  };
}

export interface WorkspaceMemberListResponse {
  data: WorkspaceMemberPayload[];
  meta: {
    page: number;
    take: number;
    item_count: number;
    total: number;
    page_count: number;
  };
}

export interface WorkspaceInvitationListResponse {
  data: WorkspaceInvitationPayload[];
  meta: {
    page: number;
    take: number;
    item_count: number;
    total: number;
    page_count: number;
  };
}

export interface WorkspacePermissionsResponse {
  permissions: string[];
  role: WorkspaceRole;
  workspace_id: string;
  user_id: string;
}

// Hook Result Types
export interface UseWorkspaceApiResult {
  // Query results
  workspaces: Workspace[];
  rawWorkspaces: WorkspacePayload[];
  recommendedWorkspace: WorkspacePayload | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  totalCount: number;

  // Counts
  workspaceCounts: WorkspaceCounts;
  workspacesCount: number;

  // Operations
  createWorkspace: (params: { name: string; description?: string }) => void;
  createWorkspaceAsync: (params: { name: string; description?: string }) => Promise<any>;
  updateWorkspace: (params: { workspaceId: string; name?: string; description?: string }) => void;
  updateWorkspaceAsync: (params: {
    workspaceId: string;
    name?: string;
    description?: string;
  }) => Promise<any>;
  deleteWorkspace: (workspaceId: string) => void;
  deleteWorkspaceAsync: (workspaceId: string) => Promise<any>;

  // Query hooks and utilities
  useWorkspace: (workspaceId?: string) => any;
  getWorkspaceById: (workspaceId: string) => Promise<Workspace>;
  refetch: () => Promise<any>;

  // Operation states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;

  // Errors
  createError: Error | null;
  updateError: Error | null;
  deleteError: Error | null;
}

export interface UseWorkspaceMembersApiResult {
  // Query results
  workspaceMembers: WorkspaceMember[];
  rawWorkspaceMembers: WorkspaceMemberPayload[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  totalCount: number;

  // Counts
  workspaceMemberCounts: WorkspaceCounts;
  workspaceMembersCount: number;

  // Operations
  inviteMember: (params: InviteWorkspaceMemberParams) => void;
  inviteMemberAsync: (params: InviteWorkspaceMemberParams) => Promise<any>;
  updateMember: (params: UpdateWorkspaceMemberParams) => void;
  updateMemberAsync: (params: UpdateWorkspaceMemberParams) => Promise<any>;
  removeMember: (params: RemoveWorkspaceMemberParams) => void;
  removeMemberAsync: (params: RemoveWorkspaceMemberParams) => Promise<any>;

  // Query hooks and utilities
  useWorkspaceMember: (memberId?: string) => any;
  getWorkspaceMemberById: (memberId: string) => Promise<WorkspaceMember>;
  refetch: () => Promise<any>;

  // Operation states
  isInviting: boolean;
  isUpdating: boolean;
  isRemoving: boolean;

  // Errors
  inviteError: Error | null;
  updateError: Error | null;
  removeError: Error | null;
}

// Upgrade-related types
export interface UpgradeInfo {
  message: string;
  action: "upgrade" | "contact_admin" | "request_access";
  requiredPlan?: string;
  currentPlan?: string;
  features?: string[];
  permission?: string;
  requiredRole?: WorkspaceRole;
}

export interface UseWorkspacePermissionsResult {
  permissions: WorkspacePermissions | null;
  role: WorkspaceRole | null;
  workspaceId: string | null;
  userId: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<any>;
  // Scoped permission checking - TypeScript enforces workspace permissions only
  canWorkspace: (
    permission: import("utils/permissions/permissionConstants").WorkspacePermission
  ) => boolean;
  hasRole: (role: WorkspaceRole) => boolean;
  getUpgradeInfo: (
    permission: import("utils/permissions/permissionConstants").WorkspacePermission
  ) => UpgradeInfo | null;

  // Core workspace permissions (product-meaningful helpers)
  canManageMembers: boolean;
  canCreateProjects: boolean;

  // Enhanced granular permissions
  canManageBilling: boolean;

  // Member management permissions
  canInviteMembers: boolean;
  canRemoveMembers: boolean;
  canUpdateMembers: boolean;
  canViewMembers: boolean;
}

// Component interfaces
export interface PermissionGuardProps {
  workspaceId?: string | number; // Optional - will be auto-detected from URL if not provided
  permission?: string;
  role?: WorkspaceRole;
  mode?: "hide" | "disable" | "show" | "tooltip";
  fallback?: React.ReactNode;
  upgradeMessage?: string;
  upgradeAction?: () => void;
  children: React.ReactNode;
}

export interface ProtectedRouteProps {
  workspaceId: string | number;
  permission?: string;
  role?: WorkspaceRole;
  redirectTo?: string;
  children: React.ReactNode;
}

export interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  upgradeInfo: UpgradeInfo;
}
